import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { curYear, getYearArray } from '@zr/utilities/date';
import { normalizeLicenseCertDate } from '@zr/utilities/form/normalizers';

import LabeledField from '@zr/components/Fields/LabeledField';
import LicenseField from '@zr/components/Fields/LicenseField';

import FormActions from '../../../components/FormActions';
import QuestionLabel from '../../../components/QuestionLabel';

import IndexedLegend from './IndexedLegend';
import FormButtons from './FormButtons';

import { patchProfileForm } from './utilities';

import './LicensesCertifications.less';

const LICENSES_CERTIFICATIONS = 'licenses_and_certifications';
const NAME = 'name';
const ISSUING_AUTHORITY = 'issuing_authority';
const ISSUED_DATE = 'issued_date';
const DESCRIPTION = 'description';

const MIN_YEAR = 1930;

export function LicensesCertifications({
  index, onContinue, onError, onSkip, profile, submitButtonText, totalScreeningQuestions,
  isInJs6650Test, setIsLoading,
}) {
  const handleContinue = async (values, formik) => {
    // `setIsLoading(true)` so that we can disable the close button while the
    // screen is processing/loading
    setIsLoading(true);
    // liccert could have empty string values from optional fields, but that doesn't seem to matter with the profile api
    const liccert = values;
    if (liccert.issued_date) {
      liccert.issued_date = normalizeLicenseCertDate(liccert.issued_date);
      liccert[`${ISSUED_DATE}_precision`] = 'Year';
    }
    await patchProfileForm({
      [LICENSES_CERTIFICATIONS]: [
        ...profile[LICENSES_CERTIFICATIONS],
        liccert,
      ],
    }, formik, onContinue, onError);
  };

  return (
    <Formik
      initialValues={{
        [NAME]: '',
        [ISSUING_AUTHORITY]: '',
        [ISSUED_DATE]: '',
        [DESCRIPTION]: '',
      }}
      onSubmit={handleContinue}
      validationSchema={Yup.object().shape({
        [NAME]: Yup.string().required('Please add a Certificate or License'),
      })}
    >
      {({
        errors, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values,
      }) => {
        // LicenseField doesn't send an event, so handle this specially
        // onSelect also uses onChange
        const handleNameChange = (name) => {
          setFieldValue(NAME, name);
        };

        return (
          <form className="Suggestion LicensesCertifications" onSubmit={handleSubmit}>
            <fieldset>
              {isInJs6650Test && (
                <QuestionLabel
                  as="legend"
                  html="If you have a Certification or License that are relevant for this position please include them below."
                  number={index}
                  totalQuestions={totalScreeningQuestions}
                />
              )}
              {!isInJs6650Test && (
                <IndexedLegend index={index} text="If you have a Certification or License that are relevant for this position please include them below." />
              )}
              <LabeledField
                as={LicenseField}
                input={{
                  disabled: isSubmitting,
                  id: `LabeledField_${NAME}`,
                  maxLength: 100,
                  name: NAME,
                  onChange: handleNameChange,
                  type: 'text',
                  value: values[NAME],
                }}
                label="Certificate or License"
                meta={{
                  error: errors[NAME],
                  touched: touched[NAME],
                }}
                placeholder="Certificate or License"
              />
              <LabeledField
                input={{
                  disabled: isSubmitting,
                  maxLength: 100,
                  name: ISSUING_AUTHORITY,
                  onChange: handleChange,
                  value: values[ISSUING_AUTHORITY],
                }}
                label="Issuing Authority (optional)"
                placeholder="Issuing Authority (optional)"
              />
              <LabeledField
                as="select"
                input={{
                  disabled: isSubmitting,
                  name: ISSUED_DATE,
                  onChange: handleChange,
                  value: values[ISSUED_DATE],
                }}
                label="Year Received (optional)"
                placeholder="Year Received (optional)"
              >
                {getYearArray(MIN_YEAR, curYear)
                  .reverse()
                  .map((year) => <option key={year} value={year}>{year}</option>)}
              </LabeledField>
              <LabeledField
                as="textarea"
                input={{
                  disabled: isSubmitting,
                  maxLength: 255,
                  name: DESCRIPTION,
                  onChange: handleChange,
                  value: values[DESCRIPTION],
                }}
                label="Description (optional)"
                placeholder="Description (optional)"
              />
              {isInJs6650Test && (
                <FormActions
                  disableContinue={isSubmitting}
                  disableSkip={isSubmitting}
                  onSkip={onSkip}
                  submitButtonText={submitButtonText}
                />
              )}
              {!isInJs6650Test && (
                <FormButtons disabled={isSubmitting} onSkip={onSkip} submitButtonText={submitButtonText} />
              )}
            </fieldset>
          </form>
        );
      }}
    </Formik>
  );
}

LicensesCertifications.propTypes = {
  index: PropTypes.number.isRequired,
  isInJs6650Test: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
  profile: PropTypes.shape({
    [LICENSES_CERTIFICATIONS]: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  setIsLoading: PropTypes.func,
  submitButtonText: PropTypes.string,
  totalScreeningQuestions: PropTypes.number.isRequired,
};

LicensesCertifications.defaultProps = {
  isInJs6650Test: false,
  onSkip: null,
  profile: {
    [LICENSES_CERTIFICATIONS]: [],
  },
  setIsLoading: () => {},
  submitButtonText: 'Continue',
};

export default LicensesCertifications;
